import {
	FaPaperPlane,
	FaStar,
	FaShieldAlt,
	FaSms,
	FaChartLine,
	FaPlus,
} from "react-icons/fa";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { apiClient } from "../../api";
import { baseUrl } from "../../Environments/environment";
import { useProperty } from "../../Contexts/PropertyContext/PropertyContext";
import { toast } from "react-toastify";

import Milestone from "./Milestone";
import KpiCard from "./KPICard";
import KPISkeleton from "./KPISkeleton";

export default function ReviewDashboard() {
	const { selectedPropertyId } = useProperty();

	const [isLoading, setIsLoading] = useState(false);
	const [negativeReviews, setNegativeReviews] = useState(0);
	const [positiveReviews, setPositiveReviews] = useState(0);
	const [totalLinkClicks, setTotalLinkClicks] = useState(0);
	const [totalReviewsCount, setTotalReviewsCount] = useState(0);
	const [totalSmsDelivered, setTotalSmsDelivered] = useState(0);
	const [totalSmsSent, setTotalSmsSent] = useState(0);

	const [currentRating, setCurrentRating] = useState(-1);
	const [currentReviewCount, setCurrentReviewCount] = useState(-1);
	const [intialRating, setInitialRating] = useState(3.7);
	const [initialReviewCount, setInitialReviewCount] = useState(80);
	const [milestones, setMilestones] = useState([]);
	const [mergedStartCurrent, setMergedStartCurrent] = useState(null);
	const [isMilestoneModalOpen, setIsMilestoneModalOpen] = useState(false);
	const [placeId, setPlaceId] = useState("");
	const [reviewsPerUser, setReviewsPerUser] = useState([]);
	const hasAutoCreatedRef = useRef(false);

	const fetchReviewsPerUser = async () => {
		setIsLoading(true);
		try {
			const token = localStorage.getItem("accessToken");
			const response = await apiClient.get(
				`${baseUrl}/review/${selectedPropertyId}/reviewsPerUser`,
				{
					headers: { Authorization: `Bearer ${token}` },
				},
			);

			setReviewsPerUser(response?.data?.result?.reviews || []);
		} catch (error) {
			console.error("Error fetching reviews per user", error);
		} finally {
			setIsLoading(false);
		}
	};
	useEffect(() => {
		fetchReviewsPerUser();
	}, [selectedPropertyId]);
	const fetchMetrics = async () => {
		try {
			setIsLoading(true);
			const token = localStorage.getItem("accessToken");
			const response = await apiClient.get(
				`${baseUrl}/reviews/metrics/${selectedPropertyId}`,
				{
					headers: { Authorization: `Bearer ${token}` },
				},
			);

			console.log(response?.data?.result);
			const d = response?.data?.result;
			if (d) {
				setNegativeReviews(d.negativeReviews);
				setPositiveReviews(d.positiveReviews);
				setTotalLinkClicks(d.totalLinkClicks);
				setTotalReviewsCount(d.totalReviewsCount);
				setTotalSmsDelivered(d.totalSmsDelivered);
				setTotalSmsSent(d.totalSmsSent);
			}
		} catch (error) {
			console.error("Error fetching", error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchMetrics();
	}, []);

	return (
		<div className="p-6 bg-gray-50 min-h-screen">
			{isLoading ? (
				<KPISkeleton />
			) : (
				<>
					{/* ================= KPIs ================= */}
					<h2 className="text-xl font-medium mb-4 flex items-center gap-2 pl-2">
						<FaChartLine className="text-blue-600" /> Key Metrics Overview
					</h2>

					<div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
						<KpiCard
							title="Review Requests Sent"
							value={totalSmsSent}
							tooltip="Total review request sent from dashboard"
							icon={<FaPaperPlane />}
							color="text-blue-600"
							sub="Links Sent"
							bgcolor="bg-blue-50 p-2 rounded-lg"
						/>

						<KpiCard
							title="Review Conversion"
							value={`${(
								((positiveReviews + negativeReviews) * 100) /
									totalReviewsCount || 1
							).toFixed(2)} %`}
							sub="Links clicked"
							tooltip="Total review link clicked (QR+ Direct Link)"
							icon={<FaChartLine />}
							color="text-purple-600"
							bgcolor="bg-purple-50 p-2 rounded-lg"
						/>

						<KpiCard
							title="Positive Reviews"
							value={positiveReviews}
							icon={<FaStar />}
							color="text-orange-500"
							bgcolor="bg-orange-50 p-2 rounded-lg"
						/>

						<KpiCard
							title="Negative Reviews Blocked"
							value={negativeReviews}
							icon={<FaShieldAlt />}
							color="text-red-500"
							bgcolor="bg-red-50 p-2 rounded-lg"
						/>

						<KpiCard
							title="SMS Delivery Rate"
							value={`${((totalSmsDelivered * 100) / totalSmsSent || 1).toFixed(
								2,
							)} %`}
							sub="Via Twilio"
							icon={<FaSms />}
							color="text-green-600"
							bgcolor="bg-green-50 p-2 rounded-lg"
						/>
					</div>
				</>
			)}

			<Milestone />
			<div className="bg-white p-5 rounded-xl shadow mt-6">
				<h3 className="font-medium text-lg mb-4 flex items-center gap-2">
					👤 Reviews Sent by Users
				</h3>

				{reviewsPerUser.length === 0 ? (
					<p className="text-gray-500">No data available</p>
				) : (
					<div className="divide-y">
						{reviewsPerUser.map((item, index) => {
							const user = item.user;

							return (
								<div
									key={user._id}
									className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between"
								>
									{/* LEFT: USER */}
									<div className="flex min-w-0 items-center gap-3">
										{/* Avatar */}
										<div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold">
											{user.firstName?.[0]}
										</div>

										<div className="min-w-0">
											<p className="font-medium text-gray-800 break-words">
												{user.firstName} {user.lastName}
											</p>
											<p className="text-sm text-gray-500 break-words">
												{user.email}
											</p>
										</div>
									</div>

									{/* RIGHT: METRICS */}
									<div className="flex items-center gap-6 self-start sm:self-auto sm:flex-shrink-0">
										{/* Reviews */}
										<div className="text-right">
											<p className="text-blue-600 font-semibold">
												{item.reviewCount}
											</p>
											<p className="text-xs text-gray-500">Reviews</p>
										</div>

										{/* Avg Rating */}
										<div className="text-right">
											<div className="text-right flex items-center gap-1 text-yellow-500 font-semibold">
												<FaStar className="text-sm" />
												{item.avgRating?.toFixed(1) || 0}
											</div>
											<p className="text-xs text-gray-500 text-right">
												Avg Rating
											</p>
										</div>
									</div>
								</div>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
}
